import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import jsPDF from "jspdf";
import Sidebar from "../../components/Sidebar";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  FileInput, Plus, Trash2, Send, Settings, Calendar,
  Search, CheckCircle2, Clock, AlertTriangle, History,
  FileText, Download, Eye, X, Users, Building2, Loader2,
  Pencil, Save
} from "lucide-react";

// Searchable select component for clients
function ClienteSearch({ clientes, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search) return clientes.slice(0, 50);
    const term = search.toLowerCase();
    return clientes.filter(c =>
      (c.nombre || "").toLowerCase().includes(term) ||
      (c.empresa || "").toLowerCase().includes(term)
    ).slice(0, 50);
  }, [clientes, search]);

  const selected = clientes.find(c => c.id === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm border rounded-lg bg-white dark:bg-slate-800 dark:border-slate-600 hover:border-blue-400 transition-colors text-left"
        data-testid="cliente-select"
      >
        <span className={selected ? "text-slate-900 dark:text-white" : "text-slate-400"}>
          {selected ? (selected.nombre || selected.empresa || "Sin nombre") : "Seleccionar cliente..."}
        </span>
        <Search className="w-4 h-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b dark:border-slate-700">
            <Input
              placeholder="Buscar cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="h-8 text-sm"
              data-testid="cliente-search-input"
            />
          </div>
          <ScrollArea className="max-h-48">
            {filtered.length === 0 ? (
              <p className="p-3 text-sm text-slate-400 text-center">No encontrado</p>
            ) : (
              filtered.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { onChange(c); setOpen(false); setSearch(""); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 ${
                    value === c.id ? "bg-blue-50 dark:bg-blue-900/20 font-medium" : ""
                  }`}
                >
                  <p className="font-medium text-slate-900 dark:text-white">{c.nombre || c.empresa || "Sin nombre"}</p>
                  {c.empresa && c.nombre && (
                    <p className="text-xs text-slate-400">{c.empresa}</p>
                  )}
                </button>
              ))
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

export default function CorrespondenciaInternaPage() {
  const { getAuthHeaders, user } = useAuth();
  const today = new Date().toISOString().split("T")[0];
  const isAdmin = ["administrador", "admin", "superadmin"].includes(user?.role?.toLowerCase() || "");
  const canEditCorrespondencia = ["administrador", "admin", "superadmin", "recepcion"].includes(user?.role?.toLowerCase() || "");

  // Data states
  const [correspondencias, setCorrespondencias] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showConfirmProcesar, setShowConfirmProcesar] = useState(false);
  const [idsParaProcesar, setIdsParaProcesar] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // View state
  const [fechaFiltro, setFechaFiltro] = useState(today);
  const [showConfig, setShowConfig] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [historialData, setHistorialData] = useState([]);
  const [historialFechaDesde, setHistorialFechaDesde] = useState("");
  const [historialFechaHasta, setHistorialFechaHasta] = useState("");
  const [historialSearch, setHistorialSearch] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ cliente_id: "", cliente_nombre: "", departamento_id: "", departamento_nombre: "", concepto: "", fecha_recepcion: "", remitente: "" });

  // Form state
  const [formData, setFormData] = useState({
    cliente_id: "", cliente_nombre: "",
    departamento_id: "", departamento_nombre: "",
    concepto: "", fecha_recepcion: today, remitente: ""
  });

  // Config form
  const [configForm, setConfigForm] = useState({
    departamento_id: "", departamento_nombre: "",
    usuario_id: "", usuario_nombre: "", usuario_email: ""
  });

  // Fetch data
  const fetchCorrespondencias = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/correspondencia-interna/`, getAuthHeaders());
      setCorrespondencias(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchClientes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mensajeria/clientes`, getAuthHeaders());
      setClientes(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchDepartamentos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/departamentos`, getAuthHeaders());
      setDepartamentos(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchUsuarios = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, getAuthHeaders());
      setUsuarios(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchConfigs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/correspondencia-interna/config/departamentos`, getAuthHeaders());
      setConfigs(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  useEffect(() => {
    Promise.all([fetchClientes(), fetchDepartamentos(), fetchUsuarios(), fetchConfigs()])
      .finally(() => setLoading(false));
  }, [fetchClientes, fetchDepartamentos, fetchUsuarios, fetchConfigs]);

  useEffect(() => { fetchCorrespondencias(); }, [fetchCorrespondencias]);

  // Add correspondence
  const handleAdd = async () => {
    if (!formData.cliente_id || !formData.departamento_id || !formData.concepto.trim()) {
      toast.error("Complete todos los campos requeridos");
      return;
    }
    try {
      await axios.post(`${API}/correspondencia-interna/`, formData, getAuthHeaders());
      toast.success("Correspondencia agregada");
      setFormData({ cliente_id: "", cliente_nombre: "", departamento_id: "", departamento_nombre: "", concepto: "", fecha_recepcion: today, urgente: false });
      setShowAddForm(false);
      fetchCorrespondencias();
    } catch (err) {
      toast.error("Error al agregar");
    }
  };

  // Delete correspondence
  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta correspondencia?")) return;
    try {
      await axios.delete(`${API}/correspondencia-interna/${id}`, getAuthHeaders());
      toast.success("Eliminada");
      fetchCorrespondencias();
      if (showHistory) fetchHistorial();
    } catch (err) { toast.error("Error al eliminar"); }
  };

  // Open edit dialog (admin)
  const openEditDialog = (item) => {
    setEditingItem(item);
    setEditForm({
      cliente_id: item.cliente_id, cliente_nombre: item.cliente_nombre,
      departamento_id: item.departamento_id, departamento_nombre: item.departamento_nombre,
      concepto: item.concepto, fecha_recepcion: item.fecha_recepcion, remitente: item.remitente || ""
    });
  };

  // Save edit (admin/recepcion)
  const handleSaveEdit = async () => {
    if (!editingItem) return;
    try {
      const payload = { ...editForm };
      if (editingItem.estado === "recibido" || editingItem.estado === "declinado") {
        payload.estado = "pendiente";
      }
      await axios.put(`${API}/correspondencia-interna/${editingItem.id}`, payload, getAuthHeaders());
      toast.success(
        (editingItem.estado === "recibido" || editingItem.estado === "declinado")
          ? "Correspondencia actualizada y marcada como pendiente"
          : "Correspondencia actualizada"
      );
      setEditingItem(null);
      fetchCorrespondencias();
      if (showHistory) fetchHistorial();
    } catch (err) { toast.error("Error al actualizar"); }
  };

  // Toggle select
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    const pendientes = correspondencias.filter(c => c.estado === "pendiente" || c.estado === "declinado").map(c => c.id);
    setSelectedIds(prev => prev.length === pendientes.length ? [] : pendientes);
  };

  // Ask confirmation before processing
  const handleProcesarClick = () => {
    const ids = selectedIds.length > 0 ? selectedIds : pendientes.map(c => c.id);
    if (ids.length === 0) {
      toast.error("No hay correspondencias pendientes para procesar");
      return;
    }
    setIdsParaProcesar(ids);
    setShowConfirmProcesar(true);
  };

  // Process after confirmation
  const handleProcesarConfirm = async () => {
    setShowConfirmProcesar(false);
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/correspondencia-interna/procesar`, { correspondencia_ids: idsParaProcesar }, getAuthHeaders());
      const data = res.data;
      data.results.forEach(r => {
        if (r.status === "procesado") {
          toast.success(`${r.departamento}: Ticket #${r.ticket_numero} asignado a ${r.asignado_a}`);
        } else {
          toast.warning(`${r.departamento}: ${r.message}`);
        }
      });
      setSelectedIds([]);
      setIdsParaProcesar([]);
      fetchCorrespondencias();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar");
    } finally { setProcessing(false); }
  };

  // Save config
  const handleSaveConfig = async () => {
    if (!configForm.departamento_id || !configForm.usuario_id) {
      toast.error("Seleccione departamento y usuario");
      return;
    }
    try {
      await axios.post(`${API}/correspondencia-interna/config/departamentos`, configForm, getAuthHeaders());
      toast.success("Configuración guardada");
      fetchConfigs();
      setConfigForm({ departamento_id: "", departamento_nombre: "", usuario_id: "", usuario_nombre: "", usuario_email: "" });
    } catch (err) { toast.error("Error al guardar"); }
  };

  const handleDeleteConfig = async (deptId) => {
    try {
      await axios.delete(`${API}/correspondencia-interna/config/departamentos/${deptId}`, getAuthHeaders());
      toast.success("Configuración eliminada");
      fetchConfigs();
    } catch (err) { toast.error("Error"); }
  };

  // Fetch history
  const fetchHistorial = async () => {
    try {
      const params = new URLSearchParams();
      if (historialFechaDesde) params.set("fecha_desde", historialFechaDesde);
      if (historialFechaHasta) params.set("fecha_hasta", historialFechaHasta);
      const res = await axios.get(`${API}/correspondencia-interna/historial?${params}`, getAuthHeaders());
      setHistorialData(res.data);
    } catch (err) { toast.error("Error al cargar historial"); }
  };

  // Generate PDF
  const generatePDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "letter" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 15;

    // Header
    doc.setFillColor(30, 58, 95);
    doc.rect(0, 0, pageWidth, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("REPORTE DE CORRESPONDENCIA INTERNA", 15, 12);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`QAsesores SRL  |  Fecha: ${fechaFiltro}  |  Generado: ${new Date().toLocaleString("es-DO")}`, 15, 20);
    yPos = 35;

    // Table
    const cols = [15, 65, 95, 155, 40, 50, 55];
    const headers = ["No.", "CLIENTE", "DEPARTAMENTO", "CONCEPTO", "REMITENTE", "FECHA REC.", "RECIBIDO POR"];
    const colWidths = [15, 50, 60, 60, 20, 30, 40];

    // Draw header
    const drawHeader = () => {
      doc.setFillColor(37, 99, 235);
      let x = 10;
      colWidths.forEach(w => { doc.rect(x, yPos - 5, w, 10, "F"); x += w; });
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      x = 10;
      headers.forEach((h, i) => { doc.text(h, x + 2, yPos); x += colWidths[i]; });
      doc.setTextColor(0, 0, 0);
      yPos += 8;
    };

    drawHeader();

    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    correspondencias.forEach((item, idx) => {
      if (yPos > pageHeight - 25) {
        doc.addPage();
        yPos = 15;
        drawHeader();
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
      }

      const rowH = 8;
      if (idx % 2 === 1) {
        doc.setFillColor(245, 247, 250);
        doc.rect(10, yPos - 5, colWidths.reduce((a, b) => a + b, 0), rowH, "F");
      }

      let x = 10;
      const rowData = [
        (idx + 1).toString(),
        item.cliente_nombre || "",
        item.departamento_nombre || "",
        item.concepto || "",
        item.remitente || "",
        item.fecha_recepcion || "",
        item.recibido_por_nombre || ""
      ];
      rowData.forEach((val, i) => {
        const lines = doc.splitTextToSize(val, colWidths[i] - 4);
        doc.text(lines[0] || "", x + 2, yPos);
        x += colWidths[i];
      });

      yPos += rowH;
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text(`QAsesores Corredores de Seguros y Reaseguros SRL  -  Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: "center" });
    }

    doc.save(`Correspondencia_Interna_${fechaFiltro}.pdf`);
    toast.success("PDF generado");
  };

  const pendientes = correspondencias.filter(c => c.estado === "pendiente" || c.estado === "declinado");
  const estadoBadge = (estado) => {
    const map = {
      pendiente: "bg-amber-100 text-amber-700 border-amber-200",
      enviado: "bg-blue-100 text-blue-700 border-blue-200",
      recibido: "bg-green-100 text-green-700 border-green-200",
      declinado: "bg-red-100 text-red-700 border-red-200"
    };
    return map[estado] || "bg-slate-100 text-slate-700";
  };

  if (loading) return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Manrope'] flex items-center gap-2">
                <FileInput className="w-6 h-6 text-blue-600" />
                Correspondencia Interna
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Registro de mensajería externa recibida
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => { setShowHistory(true); fetchHistorial(); }} data-testid="historial-btn">
                <History className="w-4 h-4 mr-2" /> Historial
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowConfig(true)} data-testid="config-btn">
                <Settings className="w-4 h-4 mr-2" /> Configurar
              </Button>
              <Button variant="outline" size="sm" onClick={generatePDF} data-testid="pdf-btn">
                <Download className="w-4 h-4 mr-2" /> PDF
              </Button>
            </div>
          </div>

          {/* Date filter + Add button */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Input type="date" value={fechaFiltro} onChange={(e) => setFechaFiltro(e.target.value)} className="w-44" data-testid="fecha-filtro" />
            </div>
            <Button onClick={() => setShowAddForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-correspondencia-btn">
              <Plus className="w-4 h-4 mr-2" /> Nueva Correspondencia
            </Button>
            <Button onClick={handleProcesarClick} disabled={processing || pendientes.length === 0} className="bg-green-600 hover:bg-green-700 text-white" data-testid="procesar-btn">
              {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Procesar {selectedIds.length > 0 ? `(${selectedIds.length} seleccionadas)` : `(${pendientes.length} pendientes)`}
            </Button>
            <div className="ml-auto text-sm text-slate-500">
              {correspondencias.length} registro(s) | {pendientes.length} por procesar
            </div>
          </div>

          {/* Table */}
          <Card className="shadow-sm dark:bg-slate-800 border-slate-200 dark:border-slate-700" data-testid="correspondencia-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-b">
                      <th className="p-3 text-center w-10">
                        <Checkbox checked={selectedIds.length === pendientes.length && pendientes.length > 0} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                      </th>
                      <th className="p-3 text-left text-xs font-semibold">CLIENTE</th>
                      <th className="p-3 text-left text-xs font-semibold">DEPARTAMENTO</th>
                      <th className="p-3 text-left text-xs font-semibold">CONCEPTO</th>
                      <th className="p-3 text-center text-xs font-semibold">REMITENTE</th>
                      <th className="p-3 text-center text-xs font-semibold">FECHA REC.</th>
                      <th className="p-3 text-center text-xs font-semibold">ESTADO</th>
                      <th className="p-3 text-center text-xs font-semibold">RECIBIDO POR</th>
                      {canEditCorrespondencia && <th className="p-3 text-center text-xs font-semibold w-20">ACCIONES</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {correspondencias.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="p-8 text-center text-slate-400">
                          <FileInput className="w-8 h-8 mx-auto mb-2 opacity-40" />
                          <p>No hay correspondencia registrada para esta fecha</p>
                        </td>
                      </tr>
                    ) : (
                      correspondencias.map((item) => (
                        <tr key={item.id} className={`border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/30 ${item.estado === "declinado" ? "!bg-orange-100 dark:!bg-orange-900/30 border-l-4 border-l-orange-400" : ""}`} data-testid={`row-${item.id}`}>
                          <td className="p-3 text-center">
                            {(item.estado === "pendiente" || item.estado === "declinado") && (
                              <Checkbox checked={selectedIds.includes(item.id)} onCheckedChange={() => toggleSelect(item.id)} />
                            )}
                          </td>
                          <td className="p-3 font-medium text-slate-900 dark:text-white">{item.cliente_nombre}</td>
                          <td className="p-3 text-slate-700 dark:text-slate-300">{item.departamento_nombre}</td>
                          <td className="p-3 text-slate-700 dark:text-slate-300 max-w-xs truncate">{item.concepto}</td>
                          <td className="p-3 text-slate-700 dark:text-slate-300 text-sm">{item.remitente || "-"}</td>
                          <td className="p-3 text-center text-slate-600 dark:text-slate-400 text-xs">{item.fecha_recepcion}</td>
                          <td className="p-3 text-center">
                            <Badge className={`${estadoBadge(item.estado)} text-xs`}>{item.estado}</Badge>
                          </td>
                          <td className="p-3 text-center text-sm text-slate-600 dark:text-slate-400">{item.recibido_por_nombre || "-"}</td>
                          {canEditCorrespondencia && (
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {(isAdmin || item.estado !== "recibido") && (
                                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)} className="text-slate-400 hover:text-blue-600" data-testid={`edit-${item.id}`}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                )}
                                {isAdmin && (
                                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-${item.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Add Form Dialog */}
        <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-600" />
                Nueva Correspondencia
              </DialogTitle>
              <DialogDescription>Registre una nueva correspondencia externa recibida</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Cliente <span className="text-red-500">*</span></Label>
                <ClienteSearch
                  clientes={clientes}
                  value={formData.cliente_id}
                  onChange={(c) => setFormData(prev => ({ ...prev, cliente_id: c.id, cliente_nombre: c.nombre || c.empresa || "Sin nombre" }))}
                />
              </div>
              <div>
                <Label>Departamento que recibe <span className="text-red-500">*</span></Label>
                <Select value={formData.departamento_id} onValueChange={(val) => {
                  const dept = departamentos.find(d => d.id === val);
                  setFormData(prev => ({ ...prev, departamento_id: val, departamento_nombre: dept?.name || "" }));
                }}>
                  <SelectTrigger data-testid="dept-select"><SelectValue placeholder="Seleccionar departamento..." /></SelectTrigger>
                  <SelectContent>
                    {departamentos.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Concepto <span className="text-red-500">*</span></Label>
                <Input value={formData.concepto} onChange={(e) => setFormData(prev => ({ ...prev, concepto: e.target.value }))} placeholder="Describa el contenido..." data-testid="concepto-input" />
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label>Fecha de Recepción</Label>
                  <Input type="date" value={formData.fecha_recepcion} onChange={(e) => setFormData(prev => ({ ...prev, fecha_recepcion: e.target.value }))} data-testid="fecha-input" />
                </div>
                <div className="flex-1">
                  <Label>Remitente</Label>
                  <Input placeholder="Persona o empresa que envía..." value={formData.remitente} onChange={(e) => setFormData(prev => ({ ...prev, remitente: e.target.value }))} data-testid="remitente-input" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancelar</Button>
                <Button onClick={handleAdd} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-correspondencia-btn">
                  <Plus className="w-4 h-4 mr-2" /> Agregar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Config Dialog */}
        <Dialog open={showConfig} onOpenChange={setShowConfig}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-slate-600" />
                Configuración de Correspondencia
              </DialogTitle>
              <DialogDescription>Asigne la persona que recibirá la correspondencia de cada departamento</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto flex-1 pr-1">
              {/* Add config */}
              <Card className="border-dashed border-blue-200 bg-blue-50/30 dark:bg-blue-900/10">
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Departamento</Label>
                      <Select value={configForm.departamento_id} onValueChange={(val) => {
                        const dept = departamentos.find(d => d.id === val);
                        setConfigForm(prev => ({ ...prev, departamento_id: val, departamento_nombre: dept?.name || "" }));
                      }}>
                        <SelectTrigger><SelectValue placeholder="Departamento..." /></SelectTrigger>
                        <SelectContent>
                          {departamentos.map(d => (
                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Persona asignada</Label>
                      <Select value={configForm.usuario_id} onValueChange={(val) => {
                        const u = usuarios.find(u => u.id === val);
                        setConfigForm(prev => ({ ...prev, usuario_id: val, usuario_nombre: u?.name || u?.email || "", usuario_email: u?.email || "" }));
                      }}>
                        <SelectTrigger><SelectValue placeholder="Usuario..." /></SelectTrigger>
                        <SelectContent>
                          {usuarios.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={handleSaveConfig} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Guardar Asignación
                  </Button>
                </CardContent>
              </Card>

              {/* Current configs */}
              <div className="space-y-2">
                  {configs.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No hay configuraciones</p>
                  ) : (
                    configs.map(c => (
                      <div key={c.departamento_id} className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="font-medium text-sm text-slate-900 dark:text-white">{c.departamento_nombre}</p>
                            <p className="text-xs text-slate-400">Asignado a: <span className="text-blue-600 font-medium">{c.usuario_nombre}</span></p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteConfig(c.departamento_id)} className="text-slate-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={showHistory} onOpenChange={(v) => { if (!v) setHistorialSearch(""); setShowHistory(v); }}>
          <DialogContent className="max-w-4xl max-h-[85vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-slate-600" />
                Historial de Correspondencia
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-center gap-3 mb-4">
              <div>
                <Label className="text-xs">Desde</Label>
                <Input type="date" value={historialFechaDesde} onChange={(e) => setHistorialFechaDesde(e.target.value)} className="w-40" />
              </div>
              <div>
                <Label className="text-xs">Hasta</Label>
                <Input type="date" value={historialFechaHasta} onChange={(e) => setHistorialFechaHasta(e.target.value)} className="w-40" />
              </div>
              <Button onClick={fetchHistorial} size="sm" className="mt-5">
                <Search className="w-4 h-4 mr-2" /> Buscar
              </Button>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={historialSearch}
                onChange={(e) => setHistorialSearch(e.target.value)}
                placeholder="Filtrar por cliente, departamento, concepto, remitente..."
                className="pl-9 h-9 text-sm"
                data-testid="historial-search-input"
              />
              {historialSearch && (
                <button onClick={() => setHistorialSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <ScrollArea className="max-h-[500px]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 dark:bg-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-300">
                    <th className="p-2 text-left">FECHA</th>
                    <th className="p-2 text-left">CLIENTE</th>
                    <th className="p-2 text-left">DEPARTAMENTO</th>
                    <th className="p-2 text-left">CONCEPTO</th>
                    <th className="p-2 text-left">REMITENTE</th>
                    <th className="p-2 text-center">ESTADO</th>
                    <th className="p-2 text-center">RECIBIDO</th>
                    {canEditCorrespondencia && <th className="p-2 text-center w-20">ACCIONES</th>}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const q = historialSearch.toLowerCase();
                    const filtered = q
                      ? historialData.filter(item =>
                          (item.cliente_nombre || "").toLowerCase().includes(q) ||
                          (item.departamento_nombre || "").toLowerCase().includes(q) ||
                          (item.concepto || "").toLowerCase().includes(q) ||
                          (item.remitente || "").toLowerCase().includes(q) ||
                          (item.estado || "").toLowerCase().includes(q) ||
                          (item.recibido_por_nombre || "").toLowerCase().includes(q)
                        )
                      : historialData;
                    return filtered.length === 0 ? (
                      <tr><td colSpan={canEditCorrespondencia ? 8 : 7} className="p-6 text-center text-slate-400">{historialSearch ? `Sin resultados para "${historialSearch}"` : "Sin resultados"}</td></tr>
                    ) : (
                      filtered.map(item => (
                      <tr key={item.id} className={`border-b border-slate-100 dark:border-slate-700 ${item.estado === "declinado" ? "!bg-orange-50 dark:!bg-orange-900/20" : ""}`}>
                        <td className="p-2 text-xs">{item.fecha_recepcion}</td>
                        <td className="p-2">{item.cliente_nombre}</td>
                        <td className="p-2">{item.departamento_nombre}</td>
                        <td className="p-2 max-w-xs truncate">{item.concepto}</td>
                        <td className="p-2 text-xs">{item.remitente || "-"}</td>
                        <td className="p-2 text-center"><Badge className={`${estadoBadge(item.estado)} text-xs`}>{item.estado}</Badge></td>
                        <td className="p-2 text-center text-xs">{item.recibido_por_nombre || "-"}</td>
                        {canEditCorrespondencia && (
                          <td className="p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {(isAdmin || item.estado !== "recibido") && (
                                <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)} className="text-slate-400 hover:text-blue-600 h-7 w-7 p-0">
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              {isAdmin && (
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-slate-400 hover:text-red-600 h-7 w-7 p-0">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                    );
                  })()}
                </tbody>
              </table>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Edit Correspondencia Dialog (Admin) */}
        {editingItem && (
          <Dialog open={!!editingItem} onOpenChange={(v) => !v && setEditingItem(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Pencil className="w-5 h-5 text-blue-600" />
                  Editar Correspondencia
                </DialogTitle>
                <DialogDescription>
                  Modificar los datos de esta correspondencia. Estado actual: <Badge className={`${estadoBadge(editingItem.estado)} text-xs ml-1`}>{editingItem.estado}</Badge>
                  {(editingItem.estado === "recibido" || editingItem.estado === "declinado") && (
                    <span className="block mt-1 text-amber-600 dark:text-amber-400 font-medium">Al actualizar, el estado cambiará a "pendiente" para ser reprocesada.</span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Cliente</Label>
                  <Select value={editForm.cliente_id} onValueChange={(val) => {
                    const c = clientes.find(cl => cl.id === val);
                    setEditForm(prev => ({ ...prev, cliente_id: val, cliente_nombre: c?.nombre || c?.empresa || "" }));
                  }}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                    <SelectContent>
                      {clientes.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.nombre || c.empresa}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Departamento</Label>
                  <Select value={editForm.departamento_id} onValueChange={(val) => {
                    const dept = departamentos.find(d => d.id === val);
                    setEditForm(prev => ({ ...prev, departamento_id: val, departamento_nombre: dept?.name || "" }));
                  }}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar departamento..." /></SelectTrigger>
                    <SelectContent>
                      {departamentos.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Concepto</Label>
                  <Input value={editForm.concepto} onChange={(e) => setEditForm(prev => ({ ...prev, concepto: e.target.value }))} />
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label>Fecha de Recepción</Label>
                    <Input type="date" value={editForm.fecha_recepcion} onChange={(e) => setEditForm(prev => ({ ...prev, fecha_recepcion: e.target.value }))} />
                  </div>
                  <div className="flex-1">
                    <Label>Remitente</Label>
                    <Input value={editForm.remitente} onChange={(e) => setEditForm(prev => ({ ...prev, remitente: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingItem(null)}>Cancelar</Button>
                <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-edit-btn">
                  <Save className="w-4 h-4 mr-2" /> Guardar Cambios
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Confirm Procesar Dialog */}
        <Dialog open={showConfirmProcesar} onOpenChange={setShowConfirmProcesar}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-green-600" />
                Confirmar Procesamiento
              </DialogTitle>
              <DialogDescription>
                Se procesarán <strong>{idsParaProcesar.length}</strong> correspondencia(s) y se crearán tickets para los departamentos correspondientes con el reporte PDF adjunto.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-700 dark:text-amber-300">
              Esta acción generará notificaciones a las personas configuradas por departamento. ¿Desea continuar?
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowConfirmProcesar(false)}>Cancelar</Button>
              <Button onClick={handleProcesarConfirm} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-procesar-btn">
                <Send className="w-4 h-4 mr-2" /> Confirmar y Procesar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
